import React, { useState, useEffect, useMemo } from "react";
import { 
  Tag, 
  Folder, 
  Package, 
  Search, 
  ArrowLeft, 
  ChevronRight, 
  TrendingUp, 
  DollarSign, 
  CheckCircle2, 
  AlertCircle, 
  ArrowRight,
  RefreshCw,
  Box
} from "lucide-react";
import { pb } from "../../services/pocketbase";
import { useCatalogContext } from "../../contexts/CatalogContext";
import { getBaseSku, cleanSku } from "../../utils/skuUtils";
import { formatMoney } from "../../utils/formatters";

/**
 * TelaTipoJoia.js — Tela de Tipo de Joia (Fase P4.2)
 *
 * Agrega produtos pelo Tipo de Joia (prefixo de SKU: RIN=Anéis, PIN=Pingentes, BER=Berloques, DIR=Formaturas, etc.):
 * - Navegação bidirecional: Tipo de Joia ↔ Categoria ↔ Ficha de Produto
 * - Lista de categorias que contêm este tipo
 * - Lista de produtos deste tipo com estatísticas de publicação, custo e vendas
 */
export default function TelaTipoJoia({ typePrefixOrId, onBack, onOpenCategory, onOpenProduct }) {
  const { catalog, loading: loadingCatalog } = useCatalogContext();
  const [produtoDadosList, setProdutoDadosList] = useState([]);
  const [tiposList, setTiposList] = useState([]);
  const [categoriasList, setCategoriasList] = useState([]);
  const [salesList, setSalesList] = useState([]);
  const [loadingDados, setLoadingDados] = useState(true);
  const [search, setSearch] = useState("");

  // Carrega dados do banco
  useEffect(() => {
    let active = true;
    const fetchData = async () => {
      setLoadingDados(true);
      try {
        const [dadosRecs, tipoRecs, catRecs] = await Promise.all([
          pb.collection("produto_dados").getFullList({ expand: "categoria,tipo_joia" }),
          pb.collection("produto_tipos").getFullList({ sort: "nome" }),
          pb.collection("produto_categorias").getFullList({ sort: "nome" })
        ]);

        if (active) {
          setProdutoDadosList(dadosRecs || []);
          setTiposList(tipoRecs || []);
          setCategoriasList(catRecs || []);
        }

        // Tenta carregar histórico de vendas para agregados do tipo
        try {
          const [ordersRes, pdvRes] = await Promise.all([
            pb.collection("production_orders").getList(1, 200, { filter: `archived != true` }),
            pb.collection("pdv_sales").getList(1, 200)
          ]);

          const allSales = [];
          ordersRes.items.forEach(o => {
            const sku = o.sku || o.full_data?.sku || "";
            const total = Number(o.total_value || o.full_data?.total_value || 0);
            if (sku) allSales.push({ skuPai: getBaseSku(sku), total });
          });

          pdvRes.items.forEach(p => {
            const sku = p.sku || p.full_data?.sku || "";
            const total = Number(p.total_amount || p.total || 0);
            if (sku) allSales.push({ skuPai: getBaseSku(sku), total });
          });

          if (active) setSalesList(allSales);
        } catch (e) {
          console.warn("Aviso ao ler vendas para agregados de tipo:", e);
        }

      } catch (err) {
        console.warn("Erro ao buscar dados de tipo de joia em P4.2:", err);
      } finally {
        if (active) setLoadingDados(false);
      }
    };

    fetchData();
    return () => { active = false; };
  }, []);

  // Resolvendo Tipo de Joia Ativo
  const activeTipo = useMemo(() => {
    if (!typePrefixOrId) return { prefix: "RIN", name: "Anel", id: null };

    const termUpper = String(typePrefixOrId).trim().toUpperCase();

    // Busca por id ou prefixo
    const matchById = tiposList.find(t => t.id === typePrefixOrId);
    if (matchById) return { prefix: matchById.prefixo_sku, name: matchById.nome, id: matchById.id };

    const matchByPrefix = tiposList.find(t => t.prefixo_sku?.toUpperCase() === termUpper);
    if (matchByPrefix) return { prefix: matchByPrefix.prefixo_sku, name: matchByPrefix.nome, id: matchByPrefix.id };

    const matchByName = tiposList.find(t => t.nome?.toUpperCase().includes(termUpper));
    if (matchByName) return { prefix: matchByName.prefixo_sku, name: matchByName.nome, id: matchByName.id };

    return { prefix: termUpper, name: termUpper, id: null };
  }, [typePrefixOrId, tiposList]);

  // Agrupamento dos Produtos Canônicos do Tipo
  const typeProducts = useMemo(() => {
    if (!catalog) return [];

    const dadosMapBySku = {};
    produtoDadosList.forEach(d => {
      if (d.sku_pai) dadosMapBySku[cleanSku(d.sku_pai)] = d;
    });

    const salesMapBySkuPai = {};
    salesList.forEach(s => {
      if (s.skuPai) {
        salesMapBySkuPai[s.skuPai] = (salesMapBySkuPai[s.skuPai] || 0) + s.total;
      }
    });

    const parentsMap = {};

    Object.values(catalog).forEach(item => {
      const rawSku = cleanSku(item.sku || item.full_data?.sku);
      if (!rawSku) return;

      const skuPai = getBaseSku(rawSku);
      if (parentsMap[skuPai]) return;

      const prefix = skuPai.split("-")[0]?.toUpperCase() || "";
      const isTypeMatch = (prefix === activeTipo.prefix.toUpperCase());

      if (isTypeMatch) {
        const dadosRec = dadosMapBySku[skuPai] || null;
        const catName = dadosRec?.expand?.categoria?.nome || item.category || "Geral";
        const custoCentavos = dadosRec?.custo_bom_centavos || 0;
        const custoBRL = custoCentavos > 0 ? custoCentavos / 100 : 0;
        const precoVendaBRL = Number(item.price || item.full_data?.price || 0);

        parentsMap[skuPai] = {
          skuPai,
          name: item.name || `Produto ${skuPai}`,
          image: item.image || item.image_url || item.image_default || null,
          category: catName,
          estadoModelagem: dadosRec?.estado_modelagem || "sem_modelo",
          custoBRL,
          precoVendaBRL,
          vendasTotalBRL: salesMapBySkuPai[skuPai] || 0,
          dadosRecord: dadosRec,
          catalogItem: item
        };
      }
    });

    return Object.values(parentsMap);
  }, [catalog, produtoDadosList, salesList, activeTipo]);

  // Categorias que possuem produtos deste Tipo
  const categoriesInType = useMemo(() => {
    const mapCat = {};
    typeProducts.forEach(p => {
      const cName = p.category;
      if (!mapCat[cName]) {
        mapCat[cName] = { name: cName, count: 0, sales: 0 };
      }
      mapCat[cName].count += 1;
      mapCat[cName].sales += p.vendasTotalBRL;
    });
    return Object.values(mapCat).sort((a, b) => b.count - a.count);
  }, [typeProducts]);

  // Filtro Interno de Busca no Tipo
  const filteredProducts = useMemo(() => {
    if (!search.trim()) return typeProducts;
    const term = cleanSku(search);
    return typeProducts.filter(p => 
      p.skuPai.includes(term) || p.name.toUpperCase().includes(term)
    );
  }, [typeProducts, search]);

  // Estatísticas Agregadas do Tipo
  const typeStats = useMemo(() => {
    const total = typeProducts.length;
    const publicados = typeProducts.filter(p => p.estadoModelagem === "publicado").length;
    const semModelo = typeProducts.filter(p => p.estadoModelagem === "sem_modelo").length;
    const somaCustos = typeProducts.reduce((acc, p) => acc + p.custoBRL, 0);
    const custoMedioBRL = total > 0 ? somaCustos / total : 0;
    const totalVendasBRL = typeProducts.reduce((acc, p) => acc + p.vendasTotalBRL, 0);

    return { total, publicados, semModelo, custoMedioBRL, totalVendasBRL };
  }, [typeProducts]);

  const handleCategoryClick = (catName) => {
    if (onOpenCategory) {
      onOpenCategory(catName);
    } else {
      window.location.hash = `#categoria/${encodeURIComponent(catName)}`;
    }
  };

  const handleProductClick = (skuPai) => {
    if (onOpenProduct) {
      onOpenProduct(skuPai);
    } else {
      window.location.hash = `#produto/${skuPai}`;
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] w-full overflow-hidden bg-slate-50">
      
      {/* CABEÇALHO DO TIPO DE JOIA (NÍVEL SUPERIOR DA HIERARQUIA) */}
      <div className="bg-slate-900 text-white px-6 py-6 space-y-4 shrink-0 shadow-lg">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          
          <div className="flex items-center gap-3">
            {onBack && (
              <button onClick={onBack} className="p-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl transition-colors">
                <ArrowLeft size={18} />
              </button>
            )}
            <div className="p-2.5 bg-amber-500 rounded-xl text-slate-900 shadow-md">
              <Tag size={24} />
            </div>
            <div>
              <span className="text-[10px] uppercase font-bold tracking-wider text-amber-400">Tipo de Joia (Hierarquia Raiz)</span>
              <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
                <span>{activeTipo.name}</span>
                <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 font-mono text-xs rounded border border-amber-500/30">
                  {activeTipo.prefix}
                </span>
              </h1>
            </div>
          </div>

          {/* ESTATÍSTICAS DO TIPO */}
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <div className="bg-slate-800 border border-slate-700 px-3.5 py-2 rounded-xl text-slate-300 font-medium">
              Total de Produtos: <strong className="text-white font-mono">{typeStats.total}</strong>
            </div>
            <div className="bg-emerald-950/80 border border-emerald-800 px-3.5 py-2 rounded-xl text-emerald-300 font-medium">
              Publicados: <strong className="text-emerald-200 font-mono">{typeStats.publicados}</strong>
            </div>
            <div className="bg-amber-950/80 border border-amber-800 px-3.5 py-2 rounded-xl text-amber-300 font-medium">
              Custo Médio BOM: <strong className="text-amber-200 font-mono">{formatMoney(typeStats.custoMedioBRL)}</strong>
            </div>
            <div className="bg-purple-950/80 border border-purple-800 px-3.5 py-2 rounded-xl text-purple-300 font-medium">
              Vendas do Tipo: <strong className="text-purple-200 font-mono">{formatMoney(typeStats.totalVendasBRL)}</strong>
            </div>
          </div>

        </div>

        {/* NAVEGAÇÃO RÁPIDA DE CATEGORIAS CONTIDAS NESTE TIPO */}
        {categoriesInType.length > 0 && (
          <div className="pt-2 border-t border-slate-800 flex items-center gap-2 overflow-x-auto [&::-webkit-scrollbar]:hidden">
            <span className="text-[11px] font-bold text-slate-400 shrink-0 uppercase tracking-wider">Categorias:</span>
            {categoriesInType.map(cat => (
              <button
                key={cat.name}
                onClick={() => handleCategoryClick(cat.name)}
                className="px-3 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 hover:text-white rounded-lg text-xs font-semibold shrink-0 border border-slate-700 flex items-center gap-1.5 transition-colors"
              >
                <Folder size={12} className="text-blue-400" />
                <span>{cat.name}</span>
                <span className="px-1.5 py-0.2 bg-slate-900 rounded-full text-[10px] font-mono text-slate-400">{cat.count}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* FILTRO DE BUSCA INTERNA NO TIPO */}
      <div className="bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between shrink-0">
        <div className="relative max-w-sm w-full">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Buscar em todos os ${activeTipo.name}...`}
            className="w-full pl-9 pr-3 py-1.5 bg-slate-100 border border-slate-200 rounded-lg text-xs font-medium focus:outline-none focus:ring-2 focus:ring-amber-500 focus:bg-white uppercase"
          />
        </div>

        <span className="text-xs font-semibold text-slate-500">
          Exibindo {filteredProducts.length} de {typeProducts.length} produtos do tipo {activeTipo.prefix}
        </span>
      </div>

      {/* GRID DE PRODUTOS DO TIPO */}
      <div className="flex-1 overflow-y-auto p-6">
        {loadingCatalog || loadingDados ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400 gap-3">
            <RefreshCw size={28} className="animate-spin text-amber-500" />
            <p className="text-sm font-medium">Carregando produtos do tipo de joia...</p>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="py-20 text-center text-slate-400 space-y-2">
            <Box size={44} className="mx-auto stroke-1 opacity-30" />
            <p className="text-base font-semibold text-slate-600">Nenhum produto encontrado para o tipo {activeTipo.name}</p>
            <p className="text-xs text-slate-400">Tente ajustar o termo de busca.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {filteredProducts.map((prod) => (
              <div
                key={prod.skuPai}
                onClick={() => handleProductClick(prod.skuPai)}
                className="bg-white border border-slate-200 hover:border-amber-400 rounded-xl p-4 shadow-sm hover:shadow-md transition-all cursor-pointer space-y-3 group flex flex-col justify-between"
              >
                <div className="space-y-3">
                  {/* IMAGEM E STATUS */}
                  <div className="w-full h-36 bg-slate-100 border border-slate-200 rounded-lg overflow-hidden relative flex items-center justify-center">
                    {prod.image ? (
                      <img src={prod.image} alt={prod.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <Package size={32} className="text-slate-300" />
                    )}

                    <span className={`absolute top-2 right-2 px-2 py-0.5 rounded text-[10px] font-bold shadow-sm ${
                      prod.estadoModelagem === "publicado"
                        ? "bg-emerald-600 text-white"
                        : prod.estadoModelagem === "pronto"
                        ? "bg-blue-600 text-white"
                        : prod.estadoModelagem === "em_modelagem"
                        ? "bg-amber-600 text-white"
                        : "bg-slate-700 text-slate-200"
                    }`}>
                      {prod.estadoModelagem}
                    </span>
                  </div>

                  {/* SKU, NOME & LINK DE CATEGORIA */}
                  <div>
                    <div className="flex items-center justify-between gap-1 mb-1">
                      <span className="px-2 py-0.5 bg-slate-900 text-white rounded font-mono text-[10px] font-bold tracking-wider inline-block">
                        {prod.skuPai}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCategoryClick(prod.category);
                        }}
                        className="text-[10px] font-semibold text-blue-600 hover:underline truncate max-w-[120px]"
                      >
                        {prod.category}
                      </button>
                    </div>
                    <h3 className="text-xs font-bold text-slate-800 line-clamp-2 group-hover:text-amber-600 transition-colors">
                      {prod.name}
                    </h3>
                  </div>
                </div>

                {/* VALORES E BOTÃO VER FICHA */}
                <div className="border-t border-slate-100 pt-3 space-y-2">
                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-400 text-[11px]">Custo BOM:</span>
                    <span className="font-bold text-slate-700 font-mono">
                      {prod.custoBRL > 0 ? formatMoney(prod.custoBRL) : "N/D"}
                    </span>
                  </div>

                  <div className="flex justify-between items-center text-xs">
                    <span className="text-slate-400 text-[11px]">Preço Venda:</span>
                    <span className="font-bold text-emerald-600 font-mono">
                      {prod.precoVendaBRL > 0 ? formatMoney(prod.precoVendaBRL) : "N/D"}
                    </span>
                  </div>

                  <div className="pt-1 flex items-center justify-between text-[11px] font-bold text-amber-600 group-hover:underline">
                    <span>Abrir Ficha do Produto</span>
                    <ArrowRight size={13} />
                  </div>
                </div>

              </div>
            ))}
          </div>
        )}
      </div>

    </div>
  );
}
